import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { Loader2, Plus, X, Building2 } from 'lucide-react';
import axios from 'axios';
import { sortStringOptions, getCRMInstitutionTypes } from '@/utils/sortOptions';
import { DISPOSITION_STATUSES } from '@/utils/dispositionStatuses';
import { getApiErrorMessage } from '@/utils/apiError';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Subscription tab content (hospital-specific). CRM users see it read-only;
// Super Admin manages plans/dates/limits in the Subscriptions module.
const SubscriptionPanel = ({ subscription, canManage }) => {
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
  if (!subscription) {
    return (
      <div className="text-sm text-gray-500 border rounded-lg p-6 text-center">
        No active subscription for this hospital.
        {canManage && (
          <p className="mt-2 text-gray-400">Assign a plan from the Subscriptions module.</p>
        )}
      </div>
    );
  }
  const rows = [
    ['Current Plan', subscription.plan_name || '—'],
    ['Status', subscription.status || '—'],
    ['Start Date', fmtDate(subscription.start_date)],
    ['End Date', fmtDate(subscription.end_date)],
    ['User Limit', subscription.total_licenses ?? subscription.license_count ?? '—'],
    ['Concurrent Users', subscription.concurrent_users ?? '—'],
    ['Grace Period', subscription.grace_period_days != null ? `${subscription.grace_period_days} days` : '—'],
    ['Discount', subscription.discount != null ? String(subscription.discount) : '—'],
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {rows.map(([label, value]) => (
          <div key={label} className="border rounded-lg p-3 bg-gray-50">
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-sm font-medium text-gray-900 capitalize">{value}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-4">
        {canManage
          ? 'To assign or change the plan, dates, or user limit, use the Subscriptions module.'
          : 'View-only. Contact a Super Admin to change subscription details.'}
      </p>
    </div>
  );
};

const LeadFormModal = ({
  isOpen, onClose, leadId = null, onSuccess = null, viewOnly = false, onEditExisting = null,
  // Hospital Management reuse: same form, converted lead is fully editable by admins,
  // plus an extra Subscription tab.
  hospitalMode = false,
  fullEditConverted = false,
  subscriptionInfo = null,
  canManageSubscription = false,
  titleOverride = null,
  onboardedByOverride = null,
}) => {
  const isEditMode = !!leadId;
  const [activeTab, setActiveTab] = useState('details');
  // Multi-contact "Select Hospital Administrator" popup (shown on conversion)
  const [adminSelectOpen, setAdminSelectOpen] = useState(false);
  const [adminSelectContacts, setAdminSelectContacts] = useState([]);
  const [adminSelectEmail, setAdminSelectEmail] = useState('');
  // Duplicate institution detection: null | { exists, is_own, owner_name, lead_id }
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateDismissed, setDuplicateDismissed] = useState(false);
  // Creator shown in the fixed header ("Added by")
  const [addedByName, setAddedByName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingLead, setLoadingLead] = useState(isEditMode);
  const [crmOfficers, setCrmOfficers] = useState([]);
  const [isConverted, setIsConverted] = useState(false);

  // Rich text editor config for Important Notes (basic formatting per requirement)
  const notesModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['clean']
    ]
  }), []);
  const notesFormats = ['bold', 'italic', 'list', 'bullet'];
  const notesIsHtml = (val) => /^\s*<(p|ul|ol|br|strong|em|b|i|div|span|h[1-6])[\s>]/i.test(val || '');

  // Institution suggestions
  const [institutionSuggestions, setInstitutionSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const institutionInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  const [formData, setFormData] = useState({
    institution_type: '',
    hospital_name: '',
    hospital_address: '',
    country: 'India',
    state: '',
    city: '',
    pin_code: '',
    contact_email: '',
    contact_phone: '',
    number_of_beds: '',
    number_of_staff: '',
    number_of_concurrent_staff: 0,
    gstin_uin: '',
    pan_it: '',
    accreditation_status: '',
    disposition_status: '',
    important_notes: '',
    lead_source: '',
    quoted_amount: 0,
    assigned_officer_email: ''
  });

  const [spocList, setSpocList] = useState([{
    spoc_name: '',
    spoc_mobile: '',
    spoc_email: '',
    spoc_designation: '',
    remarks: ''
  }]);

  const institutionTypes = getCRMInstitutionTypes();
  const bedOptions = ['Up to 50', '100', '200', '300', '500', '1000', '2000', 'Above 2000'];
  const staffOptions = ['Up to 50', '100', '200', '300', '500', '1000', '2000', 'Above 2000'];
  
  const spocDesignations = sortStringOptions([
    'Hospital Administrator',
    'Quality Assurance Team',
    'HR Manager',
    'Training Coordinator',
    'Medical Director',
    'Nursing Head'
  ]);

  const accreditationStatuses = sortStringOptions([
    'NABH Accredited',
    'NABH Inter-Level Accredited',
    'Preparing for Accreditation',
    'Not Accredited',
    'Others',
    'Information Not Available'
  ]);

  // Ordered by sales funnel — do NOT sort alphabetically
  const dispositionStatuses = DISPOSITION_STATUSES;

  const leadSources = sortStringOptions([
    'Demo Request – App Portal',
    'Organic',
    'Referral',
    'Walk-in',
    'Campaign',
    'Social Media',
    'Direct Sign-up'
  ]);

  // Fetch CRM Officers
  useEffect(() => {
    const fetchCrmOfficers = async () => {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      
      if (user.role === 'Super Admin') {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/crm/officers`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCrmOfficers(response.data || []);
        } catch (error) {
          console.error('Failed to fetch CRM Officers:', error);
        }
      }
    };
    
    if (isOpen) {
      fetchCrmOfficers();
    }
  }, [isOpen]);

  // Fetch lead data if in edit mode
  useEffect(() => {
    if (isOpen) {
      setDuplicateInfo(null);
      setDuplicateDismissed(false);
      setActiveTab('details');
    }
    if (isEditMode && leadId && isOpen) {
      setLoadingLead(true);
      const fetchLeadData = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/crm/leads/${leadId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const leadData = response.data;
          
          if (leadData.converted_at || leadData.converted_to_hospital_id || leadData.disposition_status === 'Converted') {
            setIsConverted(true);
          }
          
          setFormData({
            institution_type: leadData.institution_type || '',
            hospital_name: leadData.hospital_name || '',
            hospital_address: leadData.hospital_address || '',
            country: leadData.country || 'India',
            state: leadData.state || '',
            city: leadData.city || '',
            pin_code: leadData.pin_code || '',
            contact_email: leadData.contact_email || '',
            contact_phone: leadData.contact_phone || '',
            number_of_beds: leadData.number_of_beds || '',
            number_of_staff: leadData.number_of_staff || '',
            number_of_concurrent_staff: leadData.number_of_concurrent_staff || 0,
            gstin_uin: leadData.gstin_uin || '',
            pan_it: leadData.pan_it || '',
            accreditation_status: leadData.accreditation_status || '',
            disposition_status: leadData.disposition_status || '',
            important_notes: leadData.important_notes || '',
            lead_source: leadData.lead_source || '',
            quoted_amount: leadData.quoted_amount || 0,
            assigned_officer_email: leadData.assigned_officer_email || ''
          });
          
          // Original creator for the fixed header
          setAddedByName(leadData.lead_owner_name || leadData.created_by_name || '');

          if (leadData.spoc_details && leadData.spoc_details.length > 0) {
            setSpocList(leadData.spoc_details);
          }
        } catch (error) {
          console.error('Failed to fetch lead data:', error);
          toast.error('Failed to load lead data');
          onClose();
        } finally {
          setLoadingLead(false);
        }
      };
      
      fetchLeadData();
    } else if (!isEditMode && isOpen) {
      // Reset form for new lead
      setLoadingLead(false);
      // In create mode, the current user will be the creator
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        setAddedByName(u.full_name || u.name || u.email || '');
      } catch (e) { setAddedByName(''); }
      setFormData({
        institution_type: '',
        hospital_name: '',
        hospital_address: '',
        country: 'India',
        state: '',
        city: '',
        pin_code: '',
        contact_email: '',
        contact_phone: '',
        number_of_beds: '',
        number_of_staff: '',
        number_of_concurrent_staff: 0,
        gstin_uin: '',
        pan_it: '',
        accreditation_status: '',
        disposition_status: '',
        important_notes: '',
        lead_source: '',
        quoted_amount: 0,
        assigned_officer_email: ''
      });
      setSpocList([{
        spoc_name: '',
        spoc_mobile: '',
        spoc_email: '',
        spoc_designation: '',
        remarks: ''
      }]);
    }
  }, [isEditMode, leadId, isOpen]);

  // Click outside handler for suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        institutionInputRef.current &&
        !institutionInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Fetch institution suggestions
  const fetchInstitutionSuggestions = useCallback(
    async (query) => {
      if (query.length < 3) {
        setInstitutionSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      
      setLoadingSuggestions(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/crm/institution-names`, {
          params: { query },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const suggestions = response.data?.suggestions || [];
        setInstitutionSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } catch (error) {
        console.error('Failed to fetch institution suggestions:', error);
        setInstitutionSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    },
    []
  );

  // Real-time duplicate institution check (create mode only)
  const checkDuplicateInstitution = useCallback(async (name) => {
    const normalized = (name || '').trim();
    if (isEditMode || normalized.length < 3) {
      setDuplicateInfo(null);
      return null;
    }
    setCheckingDuplicate(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/leads/check-duplicate`, {
        params: { name: normalized },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.exists) {
        setDuplicateInfo(response.data);
        return response.data;
      }
      setDuplicateInfo(null);
      return null;
    } catch (error) {
      console.error('Duplicate check failed:', error);
      setDuplicateInfo(null);
      return null;
    } finally {
      setCheckingDuplicate(false);
    }
  }, [isEditMode]);

  const handleInstitutionNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, hospital_name: value }));
    setDuplicateInfo(null);
    setDuplicateDismissed(false);

    if (window.institutionSearchTimeout) {
      clearTimeout(window.institutionSearchTimeout);
    }

    window.institutionSearchTimeout = setTimeout(() => {
      fetchInstitutionSuggestions(value);
      checkDuplicateInstitution(value);
    }, 300);
  };

  const handleSelectSuggestion = (name) => {
    setFormData(prev => ({ ...prev, hospital_name: name }));
    setShowSuggestions(false);
    setInstitutionSuggestions([]);
    setDuplicateDismissed(false);
    checkDuplicateInstitution(name);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'number_of_staff') {
      const staffNum = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        number_of_concurrent_staff: Math.floor(staffNum * 0.1)
      }));
    }
  };

  const handleSpocChange = (index, field, value) => {
    const updated = [...spocList];
    updated[index][field] = value;
    setSpocList(updated);
  };

  const addSpoc = () => {
    setSpocList([...spocList, {
      spoc_name: '',
      spoc_mobile: '',
      spoc_email: '',
      spoc_designation: '',
      remarks: ''
    }]);
  };

  const removeSpoc = (index) => {
    if (spocList.length > 1) {
      setSpocList(spocList.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.hospital_name) {
      toast.error('Institution name is required');
      return;
    }
    if (!formData.disposition_status) {
      toast.error('Disposition status is required');
      return;
    }
    // Contact details live in the Primary Contact (SPOC) section — require at least one
    // complete contact with a valid email.
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const cleanSpocs = (spocList || [])
      .map((s) => ({ ...s }))
      .filter((s) => [s.spoc_name, s.spoc_email, s.spoc_mobile, s.spoc_designation].some((v) => (v || '').trim()));
    if (cleanSpocs.length === 0) {
      toast.error('At least one contact person is required');
      return;
    }
    for (const s of cleanSpocs) {
      if (!(s.spoc_name || '').trim()) { toast.error('Contact name is required for each contact'); return; }
      if (!emailRe.test((s.spoc_email || '').trim())) { toast.error('A valid SPOC email is required for each contact'); return; }
    }

    // Duplicate institution guard (create mode only) — re-check right before saving.
    // Surface the result in the popup (not a toast) and block the save.
    if (!isEditMode) {
      const dup = await checkDuplicateInstitution(formData.hospital_name);
      if (dup && dup.exists) {
        setDuplicateInfo(dup);
        setDuplicateDismissed(false);
        return;
      }
    }

    // If this save converts the lead and there are multiple primary contacts with emails,
    // let the user pick which one becomes the Hospital Administrator.
    const willConvert = formData.disposition_status === 'Converted' && !isConverted;
    const contactsWithEmail = cleanSpocs.filter((s) => (s.spoc_email || '').trim());
    if (willConvert && contactsWithEmail.length >= 2) {
      setAdminSelectContacts(cleanSpocs);
      setAdminSelectEmail(contactsWithEmail[0].spoc_email);
      setAdminSelectOpen(true);
      return;
    }

    await performSave(cleanSpocs, null);
  };

  const performSave = async (cleanSpocs, adminContactEmail) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const strippedNotes = (formData.important_notes || '').replace(/<(p|br|span|div)[^>]*>/gi, '').replace(/<\/(p|span|div)>/gi, '').replace(/&nbsp;/gi, '').trim();
      const normalizedNotes = strippedNotes.length === 0 ? '' : formData.important_notes;

      const payload = {
        ...formData,
        important_notes: normalizedNotes,
        spoc_details: cleanSpocs,
        number_of_concurrent_staff: parseInt(formData.number_of_concurrent_staff) || 0,
        quoted_amount: parseFloat(formData.quoted_amount) || 0
      };
      if (!payload.contact_email) delete payload.contact_email;
      if (!payload.contact_phone) delete payload.contact_phone;
      if (payload.assigned_officer_email === 'unassigned' || !payload.assigned_officer_email) {
        delete payload.assigned_officer_email;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (adminContactEmail) config.params = { admin_contact_email: adminContactEmail };

      if (isEditMode) {
        await axios.put(`${API}/crm/leads/${leadId}`, payload, config);
        toast.success('Lead updated successfully!');
      } else {
        await axios.post(`${API}/crm/leads`, payload, config);
        toast.success('Lead created successfully!');
      }

      setAdminSelectOpen(false);
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(getApiErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} lead`));
    } finally {
      setLoading(false);
    }
  };

  const showDuplicateDialog = !isEditMode && !viewOnly && !!duplicateInfo?.exists && !duplicateDismissed;
  // Common fields are disabled in view mode, or on converted leads unless full-edit is allowed
  // (e.g. Super Admin editing via Hospital Management, where lead == hospital record).
  const editingDisabled = viewOnly || (isConverted && !fullEditConverted);

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
        {/* Fixed header — stays visible while the form body scrolls */}
        <DialogHeader className="px-6 pt-6 pb-0 border-b shrink-0 text-left">
          <DialogTitle>
            {titleOverride || (viewOnly ? 'Lead Details' : (isConverted ? 'View Lead (Converted to Institution)' : (isEditMode ? 'Edit Lead' : 'Add New Lead')))}
          </DialogTitle>
          {(onboardedByOverride || addedByName) && (
            <p className="text-sm text-gray-500 mt-1">
              {hospitalMode ? 'Onboarded by' : 'Added by'}: <span className="font-medium text-gray-700">{onboardedByOverride || addedByName}</span>
            </p>
          )}
          {/* Tabs (Hospital Management reuse) */}
          {hospitalMode && (
            <div className="flex gap-6 mt-3 -mb-px">
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'details' ? 'border-[#0056B3] text-[#0056B3]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Hospital Details
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('subscription')}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'subscription' ? 'border-[#0056B3] text-[#0056B3]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Subscription
              </button>
            </div>
          )}
        </DialogHeader>

        {loadingLead ? (
          <div className="flex items-center justify-center py-8 flex-1">
            <Loader2 className="w-8 h-8 animate-spin text-[#0056B3]" />
          </div>
        ) : activeTab === 'subscription' ? (
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <SubscriptionPanel subscription={subscriptionInfo} canManage={canManageSubscription} />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 flex-1 overflow-y-auto px-6 py-4">
            {isConverted && !viewOnly && !fullEditConverted && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                <p className="text-sm text-yellow-800">
                  This lead has been converted to an institution. Other institution details are managed on the Hospital record;
                  you can still update <span className="font-medium">contact persons (SPOCs)</span> here and they will sync to the hospital.
                </p>
              </div>
            )}
            
            {/* Basic Information */}
            <div>
              <h3 className="font-semibold text-sm mb-4">Institution Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type of Institution *</Label>
                  <Select value={formData.institution_type} onValueChange={(value) => setFormData({ ...formData, institution_type: value })} disabled={editingDisabled}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {institutionTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="relative">
                  <Label>Institution Name *</Label>
                  <Input 
                    ref={institutionInputRef}
                    name="hospital_name" 
                    value={formData.hospital_name} 
                    onChange={handleInstitutionNameChange} 
                    placeholder="Enter name" 
                    disabled={editingDisabled}
                    autoComplete="off"
                  />
                  {showSuggestions && institutionSuggestions.length > 0 && (
                    <div
                      ref={suggestionsRef}
                      className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                    >
                      {institutionSuggestions.map((name, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSelectSuggestion(name)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-50 last:border-b-0"
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <Label>Address *</Label>
                  <Textarea name="hospital_address" value={formData.hospital_address} onChange={handleChange} placeholder="Enter address" rows={2} disabled={editingDisabled} />
                </div>
                <div>
                  <Label>Country *</Label>
                  <Input name="country" value={formData.country} disabled={editingDisabled} />
                </div>
                <div>
                  <Label>State *</Label>
                  <Input name="state" value={formData.state} onChange={handleChange} placeholder="Enter state" disabled={editingDisabled} />
                </div>
                <div>
                  <Label>City *</Label>
                  <Input name="city" value={formData.city} onChange={handleChange} placeholder="Enter city" disabled={editingDisabled} />
                </div>
                <div>
                  <Label>Pin Code *</Label>
                  <Input name="pin_code" value={formData.pin_code} onChange={handleChange} placeholder="Enter pin code" disabled={editingDisabled} />
                </div>
                <div>
                  <Label>Number of Beds</Label>
                  <Select value={formData.number_of_beds} onValueChange={(value) => setFormData({ ...formData, number_of_beds: value })} disabled={editingDisabled}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {bedOptions.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Number of Staff</Label>
                  <Select value={formData.number_of_staff} onValueChange={(value) => setFormData({ ...formData, number_of_staff: value })} disabled={editingDisabled}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {staffOptions.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Lead Information */}
            <div>
              <h3 className="font-semibold text-sm mb-4">Lead Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Disposition Status *</Label>
                  <Select value={formData.disposition_status} onValueChange={(value) => setFormData({ ...formData, disposition_status: value })} disabled={editingDisabled}>
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      {dispositionStatuses.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Lead Source</Label>
                  <Select value={formData.lead_source} onValueChange={(value) => setFormData({ ...formData, lead_source: value })} disabled={editingDisabled}>
                    <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>
                      {leadSources.map(source => (
                        <SelectItem key={source} value={source}>{source}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quoted Amount</Label>
                  <Input type="number" name="quoted_amount" value={formData.quoted_amount} onChange={handleChange} placeholder="₹" disabled={editingDisabled} />
                </div>
                <div>
                  <Label>Accreditation Status</Label>
                  <Select value={formData.accreditation_status} onValueChange={(value) => setFormData({ ...formData, accreditation_status: value })} disabled={editingDisabled}>
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      {accreditationStatuses.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Important Notes</Label>
                  {viewOnly ? (
                    formData.important_notes ? (
                      notesIsHtml(formData.important_notes) ? (
                        <div className="ql-snow mt-1 border rounded-md bg-gray-50">
                          <div
                            className="ql-editor text-sm text-gray-900"
                            dangerouslySetInnerHTML={{ __html: formData.important_notes }}
                          />
                        </div>
                      ) : (
                        <p className="mt-1 text-sm text-gray-900 border rounded-md p-3 bg-gray-50 whitespace-pre-wrap">{formData.important_notes}</p>
                      )
                    ) : (
                      <p className="mt-1 text-sm text-gray-500">—</p>
                    )
                  ) : (
                    <div className="bg-white rounded-md [&_.ql-editor]:min-h-[90px] [&_.ql-editor]:max-h-[160px] [&_.ql-editor]:overflow-y-auto">
                      <ReactQuill
                        theme="snow"
                        value={formData.important_notes || ''}
                        onChange={(value) => setFormData(prev => ({ ...prev, important_notes: value }))}
                        modules={notesModules}
                        formats={notesFormats}
                        readOnly={editingDisabled}
                        placeholder="Add any notes"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Primary Contact (SPOC) — supports multiple contact persons */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-sm">Primary Contact Details</h3>
                {!viewOnly && (
                  <Button type="button" onClick={addSpoc} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" /> Add Another Contact
                  </Button>
                )}
              </div>
              {spocList.map((spoc, index) => (
                <div key={index} className="border rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Contact {index + 1}</h4>
                    {spocList.length > 1 && !viewOnly && (
                      <Button type="button" onClick={() => removeSpoc(index)} variant="ghost" size="sm">
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Contact Name *</Label>
                      <Input value={spoc.spoc_name} onChange={(e) => handleSpocChange(index, 'spoc_name', e.target.value)} placeholder="Enter name" disabled={viewOnly} />
                    </div>
                    <div>
                      <Label>Mobile Number</Label>
                      <Input value={spoc.spoc_mobile} onChange={(e) => handleSpocChange(index, 'spoc_mobile', e.target.value)} placeholder="Enter mobile" disabled={viewOnly} />
                    </div>
                    <div>
                      <Label>SPOC Email *</Label>
                      <Input type="email" value={spoc.spoc_email} onChange={(e) => handleSpocChange(index, 'spoc_email', e.target.value)} placeholder="Enter email" disabled={viewOnly} />
                    </div>
                    <div>
                      <Label>Designation</Label>
                      <Select value={spoc.spoc_designation} onValueChange={(value) => handleSpocChange(index, 'spoc_designation', value)} disabled={viewOnly}>
                        <SelectTrigger><SelectValue placeholder="Select designation" /></SelectTrigger>
                        <SelectContent>
                          {spocDesignations.map(des => (
                            <SelectItem key={des} value={des}>{des}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Form Actions */}
            <div className="flex gap-3 justify-end pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              {!viewOnly && (
                <Button type="submit" className="bg-[#0056B3] hover:bg-[#0056B3]/90" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    hospitalMode ? 'Update Hospital' : (isConverted ? 'Update Contacts' : (isEditMode ? 'Update Lead' : 'Create Lead'))
                  )}
                </Button>
              )}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>

    {/* Duplicate institution popup */}
    <Dialog
      open={showDuplicateDialog}
      onOpenChange={(open) => { if (!open) setDuplicateDismissed(true); }}
    >
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Institution already exists</DialogTitle>
        </DialogHeader>
        {duplicateInfo?.is_own ? (
          <>
            <p className="text-sm text-gray-700">
              This institution already exists in your leads. Would you like to edit the existing lead?
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDuplicateDismissed(true)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                onClick={() => {
                  const existingId = duplicateInfo?.lead_id;
                  setDuplicateDismissed(true);
                  setDuplicateInfo(null);
                  if (onEditExisting && existingId) {
                    onEditExisting(existingId);
                  }
                }}
              >
                Edit Existing
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700">
              This institution has already been added by <span className="font-medium">{duplicateInfo?.owner_name}</span>. Please contact the owner or your administrator if any updates are required.
            </p>
            <div className="flex justify-end pt-2">
              <Button
                type="button"
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                onClick={() => setDuplicateDismissed(true)}
              >
                OK
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Select Hospital Administrator (multiple primary contacts) */}
    <Dialog open={adminSelectOpen} onOpenChange={(open) => { if (!open) setAdminSelectOpen(false); }}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Select Hospital Administrator</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">
          Multiple primary contacts are available for this institution. Please select the contact who should be assigned as the Hospital Administrator.
        </p>
        <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto">
          {adminSelectContacts.filter((s) => (s.spoc_email || '').trim()).map((s, i) => (
            <label
              key={i}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${adminSelectEmail === s.spoc_email ? 'border-[#0056B3] bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
            >
              <input
                type="radio"
                name="admin-contact"
                className="mt-1"
                checked={adminSelectEmail === s.spoc_email}
                onChange={() => setAdminSelectEmail(s.spoc_email)}
              />
              <div className="text-sm">
                <p className="font-medium text-gray-900">{s.spoc_name || '—'} <span className="text-gray-400 font-normal">· {s.spoc_designation || '—'}</span></p>
                <p className="text-gray-600">{s.spoc_email}</p>
                <p className="text-gray-500">{s.spoc_mobile || '—'}</p>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-3 pt-3">
          <Button type="button" variant="outline" onClick={() => setAdminSelectOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
            disabled={loading || !adminSelectEmail}
            onClick={() => performSave(adminSelectContacts, adminSelectEmail)}
          >
            {loading ? 'Saving…' : 'Assign & Convert'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default LeadFormModal;
